import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import VehicleImageUpload from "./VehicleImageUpload";

interface VehicleData {
  brand: string;
  model: string;
  year: number | null;
  color: string;
  fuel_type: string;
  license_plate: string;
  registration_date: string | null;
  transmission_type: string;
  apk_date: string | null;
  [key: string]: string | number | null | undefined; // Voor eventuele andere velden
}

interface VehicleDetailsProps {
  vehicleData: VehicleData | null;
}

const VehicleDetails = ({ vehicleData }: VehicleDetailsProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  };

  const saveVehicle = async () => {
    if (!vehicleData) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { error } = await supabase
        .from('vehicles')
        .insert({
          brand: vehicleData.brand,
          model: vehicleData.model,
          year: vehicleData.year,
          color: vehicleData.color,
          fuel_type: vehicleData.fuel_type,
          license_plate: vehicleData.license_plate,
          registration_date: formatDate(vehicleData.registration_date),
          transmission_type: vehicleData.transmission_type,
          apk_date: formatDate(vehicleData.apk_date),
          display_name: displayName || `${vehicleData.brand} ${vehicleData.model}`,
          image_url: imageUrl,
          user_id: user.id
        });

      if (error) throw error;

      toast({
        title: "Voertuig toegevoegd",
        description: "Het voertuig is succesvol toegevoegd aan je wagenpark.",
      });

      navigate('/app');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Onbekende fout bij opslaan';
      toast({
        variant: "destructive",
        title: "Fout bij opslaan",
        description: errorMessage,
      });
    }
  };

  const handleImageUploaded = (url: string) => {
    setImageUrl(url);
  };

  const vehicleFields = [
    { label: "Merk", value: vehicleData?.brand },
    { label: "Model", value: vehicleData?.model },
    { label: "Bouwjaar", value: vehicleData?.year },
    { label: "Kleur", value: vehicleData?.color },
    { label: "Brandstof", value: vehicleData?.fuel_type },
    { label: "Kenteken", value: vehicleData?.license_plate },
    { label: "Registratie Datum", value: vehicleData?.registration_date },
    { label: "Transmissie", value: vehicleData?.transmission_type },
    { label: "APK Vervaldatum", value: vehicleData?.apk_date },
  ];

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Voertuig Afbeelding</h3>
        <VehicleImageUpload onImageUploaded={handleImageUploaded} />
        {imageUrl && (
          <div className="mt-4">
            <img 
              src={imageUrl} 
              alt="Voertuig afbeelding" 
              className="max-w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Voertuig Naam</h3>
        <Input
          placeholder={vehicleData ? `${vehicleData.brand} ${vehicleData.model}` : ''}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Voertuig Details</h3>
        <Table>
          <TableBody>
            {vehicleFields.map(({ label, value }, index) => (
              value && (
                <TableRow 
                  key={label} 
                  className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-50`}
                >
                  <TableCell className="text-sm text-gray-600 font-medium border-none">{label}</TableCell>
                  <TableCell className="text-gray-900 text-right border-none">{value}</TableCell>
                </TableRow>
              )
            ))}
          </TableBody>
        </Table>
      </div>

      <Button
        className="w-full h-12 bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
        onClick={saveVehicle}
      >
        Voertuig Toevoegen
      </Button>
    </div>
  );
};

export default VehicleDetails;
